import { Component, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LifecycleAware } from '@lsq-frontend/common-ui';
import { ClaimService, FeatureFlagService } from '@lsq-frontend/common-ui';
import {
    GetAnalyticsReportUrlService,
    GetAnalyticsReportUrlRequest
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer/insights/get-analytics-report-url';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { combineLatest, from, Observable, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { TABLEAU_EMBED_QUERY_PARAMS_DEFAULT } from '../../buyer/insights/insights.configuration';
import { BuyerInsightsService } from '../../buyer/insights/insights.service';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import { ReportType } from '../../shared/lsq-high-charts/charts/chart-types';
@LifecycleAware()
@Component({
    selector: 'account-spend-volume-tile',
    styleUrls: ['./spend-volume-tile.component.scss'],
    encapsulation: ViewEncapsulation.None,
    template: `
        <card class="spend-volume-card">
            <div card-header>
                <h3
                    id="spend-volume-card-heading"
                    i18n="Header for the Spend Volume tile in the Account Experience"
                >
                    Spend Volume
                </h3>
            </div>
            <div class="spend-volume-card-body" card-body>
                <div
                    *ngIf="
                        isFinancingAccountPortalUser ||
                        ((buyerAnalyticsFeatureIsOn$ | async) === false &&
                            (buyerAnalyticsHighChartsFeatureIsOn$ | async) === false)
                    "
                    class="spend-volume-card-body-inner no-data"
                >
                    <img
                        class="no-data-image"
                        src="apps/fasttrack/src/assets/images/no-data-bars.png"
                        alt="No data to show for Spend Volume"
                    />
                    <h4 class="no-data-heading">No data available</h4>
                    <p class="no-data-body">Spend Volume coming soon.</p>
                </div>
                <div
                    *ngIf="
                        !isFinancingAccountPortalUser &&
                        (buyerAnalyticsFeatureIsOn$ | async) === undefined &&
                        (buyerAnalyticsHighChartsFeatureIsOn$ | async) === false
                    "
                >
                    <lsq-circle-loader></lsq-circle-loader>
                </div>
                <iframe
                    *ngIf="
                        !isFinancingAccountPortalUser &&
                        (buyerAnalyticsFeatureIsOn$ | async) &&
                        !(buyerAnalyticsHighChartsFeatureIsOn$ | async)
                    "
                    id="buyer-overview-spend-volume-frame"
                    [src]="spendVolumeReportUrl$ | async"
                    width="100%"
                    height="256"
                    [style]="{ border: 'none' }"
                ></iframe>
                <div
                    [id]="'spend-volume-overview-chart'"
                    *ngIf="
                        !isFinancingAccountPortalUser &&
                        (buyerAnalyticsHighChartsFeatureIsOn$ | async)
                    "
                >
                    <lsq-high-chart
                        [id]="ReportType.SpendVolumeOverview"
                        [type]="ReportType.SpendVolumeOverview"
                    ></lsq-high-chart>
                </div>
            </div>
        </card>
    `
})
export class AccountSpendVolumeTileComponent {
    public isFinancingAccountPortalUser = false;

    public buyerAnalyticsHighChartsFeatureIsOn$: Observable<boolean | undefined> = combineLatest([
        this._featureFlagService.isOn(FeatureFlags.BuyerExperienceAnalyticsOverview),
        this._featureFlagService.isOn(FeatureFlags.BuyerExperienceHighCharts)
    ]).pipe(
        map(([featureFlagIsOn, highChartsFlagIsOn]) => featureFlagIsOn && highChartsFlagIsOn),
        startWith(undefined)
    );

    public buyerAnalyticsFeatureIsOn$: Observable<boolean | undefined> = combineLatest([
        this._featureFlagService.isOn(FeatureFlags.BuyerExperienceAnalyticsOverview),
        from(this._buyerInsightsService.isActivatedForBuyer())
    ]).pipe(
        map(([featureFlagIsOn, buyerIsActivated]) => featureFlagIsOn && buyerIsActivated),
        startWith(undefined)
    );

    public ReportType = ReportType;

    public spendVolumeReportUrl$: Observable<SafeResourceUrl | undefined> = combineLatest([
        this.buyerAnalyticsFeatureIsOn$,
        this.buyerAnalyticsHighChartsFeatureIsOn$
    ]).pipe(
        map(([featureFlagIsOn, highChartsFlagIsOn]) => featureFlagIsOn && !highChartsFlagIsOn),
        switchMap((shouldDisplayAnalytics) =>
            shouldDisplayAnalytics
                ? from(
                      this._getAnalyticsReportUrlService
                          .executeOnce(
                              new GetAnalyticsReportUrlRequest({
                                  reportName: 'SPEND_VOLUME_LANDING'
                              })
                          )
                          .then(({ reportUrl }) =>
                              this._domSanitizer.bypassSecurityTrustResourceUrl(
                                  reportUrl +
                                      TABLEAU_EMBED_QUERY_PARAMS_DEFAULT +
                                      '&:device=phone&:toolbar=no'
                              )
                          )
                  )
                : of(undefined)
        )
    );

    constructor(
        private _getAnalyticsReportUrlService: GetAnalyticsReportUrlService,
        private _domSanitizer: DomSanitizer,
        private _featureFlagService: FeatureFlagService,
        private _claimService: ClaimService,
        private _buyerInsightsService: BuyerInsightsService
    ) {
        this.isFinancingAccountPortalUser = this._claimService.isOnSync(
            DashboardClaim.FinancingAccountPortal
        );
    }
}
